import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import logo from "@/assets/logo.jpeg";
import { CONTACT } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  const navLinks = [
    { label: t("nav.about"), id: "about" },
    { label: t("nav.story"), id: "story" },
    { label: t("nav.products"), id: "products" },
    { label: t("nav.markets"), id: "markets" },
    { label: t("nav.values"), id: "values" },
    { label: t("nav.faq"), id: "faq" },
    { label: t("nav.contact"), id: "contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-2xl border-b border-[#40916c]/15 shadow-[0_6px_35px_rgba(45,106,79,0.08)]"
            : "bg-white/75 backdrop-blur-lg border-b border-white/50 shadow-sm"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-[64px] sm:h-[76px] md:h-[92px] flex items-center justify-between">
          <button
            onClick={() => go("hero")}
            className="flex items-center gap-2.5 sm:gap-3.5 group text-left shrink-0"
            data-testid="nav-logo"
          >
            <img
              src={logo}
              alt="Gupta Enterprises logo"
              className="h-10 w-10 sm:h-11 sm:w-11 md:h-13 md:w-13 rounded-full object-cover ring-1 sm:ring-2 ring-[#40916c]/25 group-hover:ring-[#40916c]/60 shadow-sm transition-all duration-300 shrink-0"
            />
            <span className="flex flex-col leading-none text-left">
              <span className="font-display font-bold text-[14px] sm:text-base md:text-xl tracking-tight text-[#2d6a4f] group-hover:text-[#1b4332] transition-colors">
                Gupta Enterprises
              </span>
              <span className="font-body text-[9px] sm:text-[10px] md:text-[11px] tracking-[0.22em] sm:tracking-[0.28em] uppercase text-[#40916c] font-semibold mt-0.5 sm:mt-1">
                Vasavi Gold
              </span>
            </span>
          </button>

          <div className="hidden lg:flex items-center gap-8 lg:gap-9">
            {navLinks.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                className="font-body text-[15px] font-medium text-[#2d6a4f] hover:text-[#40916c] transition-colors relative group py-1"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-[#40916c] rounded-full transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 sm:gap-3.5 shrink-0">
            {/* Language Switcher Pill — Hidden on small mobile screens to keep header spacious */}
            <div className="hidden sm:flex items-center gap-1 rounded-full border border-[#40916c]/30 p-1 bg-white/80 backdrop-blur-md shadow-sm">
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${
                  lang === "en"
                    ? "bg-[#40916c] text-white shadow-sm"
                    : "text-[#3a4a41] hover:text-[#40916c]"
                }`}
                data-testid="lang-toggle-en"
                aria-label="Switch to English"
              >
                EN
              </button>
              <button
                onClick={() => setLang("te")}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${
                  lang === "te"
                    ? "bg-[#40916c] text-white shadow-sm"
                    : "text-[#3a4a41] hover:text-[#40916c]"
                }`}
                data-testid="lang-toggle-te"
                aria-label="Switch to Telugu"
              >
                తెలుగు
              </button>
            </div>

            <a
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              onClick={() => go("contact")}
              className="hidden md:inline-flex items-center rounded-full bg-[#40916c] hover:bg-[#2d6a4f] text-white font-body text-[15px] font-semibold px-6 py-3 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#40916c]/30"
              data-testid="nav-enquiry-btn"
            >
              {t("hero.enquiryBtn")}
            </a>
            
            {/* Mobile Hamburger Toggle Button */}
            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 sm:h-10 sm:w-10 rounded-full border border-[#40916c]/30 text-[#2d6a4f] bg-white/90 shadow-sm"
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-[#40916c]/15 overflow-hidden shadow-xl"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              <div className="flex items-center justify-between py-2.5 border-b border-[#40916c]/10 mb-2">
                <span className="flex items-center gap-2 font-body text-xs font-semibold text-[#5b6b62]">
                  <Globe size={15} /> {t("nav.language")}
                </span>
                <div className="flex items-center gap-1 rounded-full border border-[#40916c]/30 p-1 bg-white shadow-sm">
                  <button
                    onClick={() => setLang("en")}
                    className={`px-3.5 py-1 text-xs font-bold rounded-full ${
                      lang === "en" ? "bg-[#40916c] text-white" : "text-[#3a4a41]"
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => setLang("te")}
                    className={`px-3.5 py-1 text-xs font-bold rounded-full ${
                      lang === "te" ? "bg-[#40916c] text-white" : "text-[#3a4a41]"
                    }`}
                  >
                    తెలుగు
                  </button>
                </div>
              </div>

              {navLinks.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-body font-medium text-[#2d6a4f] py-3.5 border-b border-[#40916c]/5"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 text-center rounded-full bg-[#40916c] text-white font-body font-semibold px-6 py-3.5 shadow-md"
                data-testid="nav-mobile-enquiry-btn"
              >
                {t("hero.enquiryBtn")}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
